import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatErr } from '@/lib/api';
import { toast } from 'sonner';
import { Activity, Users, Coffee, AlertTriangle, ChevronRight } from 'lucide-react';

const POLL_MS = 30 * 1000;
const DOT = {
  active: 'bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.15)]',
  away: 'bg-amber-500 shadow-[0_0_0_4px_rgba(245,158,11,0.15)]',
  offline: 'bg-red-500 shadow-[0_0_0_4px_rgba(239,68,68,0.15)]',
  on_break: 'bg-violet-500 shadow-[0_0_0_4px_rgba(139,92,246,0.15)]',
  off_duty: 'bg-zinc-400 shadow-[0_0_0_4px_rgba(161,161,170,0.15)]',
};
const LABEL = { active: 'Active', away: 'Away', offline: 'Offline', on_break: 'On Break', off_duty: 'Off Duty' };
const TEXT = { active: 'text-emerald-500', away: 'text-amber-500', offline: 'text-red-500', on_break: 'text-violet-500', off_duty: 'text-zinc-500' };
const ACTION_LABEL = {
  opened_profile: 'Opened profile',
  created_candidate: 'Added lineup',
  updated_candidate: 'Updated lineup',
  logged_call: 'Logged call',
  scheduled_interview: 'Scheduled interview',
  uploaded_cv: 'Uploaded CV',
  created_call_candidate: 'Added manual candidate',
};
const EMPTY_COUNTS = { active: 0, away: 0, offline: 0, on_break: 0, off_duty: 0, idle: 0 };

function fmtIST(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }); }
  catch { return ''; }
}
function seenLabel(mins) {
  if (mins === null || mins === undefined) return 'never';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function TeamActivity() {
  const [data, setData] = useState({ items: [], counts: EMPTY_COUNTS, break_active: false, break_window: null });

  const load = async () => {
    try { const res = await api.get('/team-activity'); setData(res.data); }
    catch (e) { if (e?.response?.status && e.response.status !== 403) toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, []);

  const counts = data.counts || EMPTY_COUNTS;
  const bw = data.break_window;

  return (
    <div className="tactical-card p-6 mb-8" data-testid="team-activity">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Live · refreshes every 30s · tracked only between punch-in and punch-out</div>
          <div className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" /> Team Activity
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs flex-wrap" data-testid="team-activity-legend">
          <span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${DOT.active}`} /><span className="font-mono font-bold">{counts.active}</span> active</span>
          <span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${DOT.away}`} /><span className="font-mono font-bold">{counts.away}</span> away</span>
          <span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${DOT.on_break}`} /><span className="font-mono font-bold">{counts.on_break}</span> on break</span>
          <span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${DOT.offline}`} /><span className="font-mono font-bold">{counts.offline}</span> offline</span>
          <span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${DOT.off_duty}`} /><span className="font-mono font-bold">{counts.off_duty}</span> off duty</span>
          {counts.idle > 0 && (
            <span className="flex items-center gap-1.5 text-amber-600 font-black" data-testid="ta-idle-badge">
              <AlertTriangle className="w-3 h-3" /> {counts.idle} idle
            </span>
          )}
        </div>
      </div>

      {data.break_active ? (
        <div data-testid="break-banner-active" className="mb-4 flex items-center gap-3 p-3 rounded-md border border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300">
          <Coffee className="w-4 h-4" />
          <div className="text-sm"><span className="font-black">Lunch break in progress</span><span className="text-xs ml-2 opacity-80">{bw?.start}–{bw?.end} IST · offline alerts paused</span></div>
        </div>
      ) : bw?.enabled ? (
        <div data-testid="break-banner-scheduled" className="mb-4 text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Coffee className="w-3 h-3" /> Lunch break window: {bw.start}–{bw.end} IST
        </div>
      ) : null}

      {data.items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center flex flex-col items-center gap-2">
          <Users className="w-6 h-6" /> No team members yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="team-activity-grid">
          {data.items.map(m => {
            const target = m.daily_target || 0;
            const done = m.calls_today || 0;
            const pct = target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 0;
            const la = m.last_action;
            return (
              <Link
                key={m.user_id} to={`/team?user=${m.user_id}`}
                data-testid={`ta-row-${m.user_id}`}
                className="flex flex-col p-3 rounded-md border border-border/60 hover:border-primary/40 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-2.5 h-2.5 rounded-full ${DOT[m.status] || DOT.off_duty}`} data-testid={`ta-dot-${m.status}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      {m.name}
                      {m.is_idle && <span data-testid={`ta-idle-${m.user_id}`} className="text-[9px] uppercase tracking-widest font-black text-amber-600 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded"><AlertTriangle className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />idle {fmtIST(m.idle_since)}</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-widest truncate">{m.role}{m.designation ? ` · ${m.designation}` : ''}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-[10px] uppercase tracking-widest font-black ${TEXT[m.status] || TEXT.off_duty}`}>{LABEL[m.status] || m.status}</div>
                    <div className="text-[11px] text-muted-foreground">{m.status === 'off_duty' ? (m.punch_out ? 'signed off' : 'not started') : seenLabel(m.last_seen_minutes_ago)}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* Progress bar */}
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-border/60 rounded-full overflow-hidden">
                    <div data-testid={`ta-progress-${m.user_id}`} className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="font-mono text-xs tabular-nums">
                    <span className="font-black text-foreground">{done}</span>
                    <span className="text-muted-foreground">/{target || '—'}</span>
                  </span>
                </div>

                {/* Last action */}
                <div className="mt-1.5 text-[11px] text-muted-foreground truncate" data-testid={`ta-lastaction-${m.user_id}`}>
                  {la ? <>{ACTION_LABEL[la.action] || la.action}{la.target_name ? ` · ${la.target_name}` : ''} <span className="font-mono">— {fmtIST(la.ts)}</span></> : <span className="italic">No actions yet today</span>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
